import React, { useState, useRef } from 'react';
import Formsy from 'formsy-react';
import {
  TextFieldFormsy,
  CheckboxFormsy,
  RemoteChipSelectFormsy,
  SelectFormsy,
  ImageUploadFormsy,
} from 'components/formsy';

import { styled } from '@mui/material/styles';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';

import { generateNumberValidation, generateTextValidation } from 'utils/generateValidation';
import { OrganisationDto } from 'models/dtoTypes';
import { GetOrganisations } from 'services/openapiClient';
import { transformOptionData } from 'utils/form';
import { useTranslation } from 'react-i18next';

const PREFIX = 'FormContainer';

const classes = {
  root: `${PREFIX}-root`,
  button: `${PREFIX}-button`,
  submitButton: `${PREFIX}-submitButton`,
  flashSpacer: `${PREFIX}-flashSpacer`,
  paper: `${PREFIX}-paper`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.submitButton}`]: {
    minWidth: 100,
    height: 50,
  },

  [`& .${classes.flashSpacer}`]: {
    minHeight: 10,
  },

  [`& .${classes.paper}`]: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

export interface IField<TAttributes> {
  readonly?: boolean | ((attributes?: TAttributes) => boolean);
  label?: string;
  widget: string;
  formatter?: (value: string | boolean | number, attributes?: TAttributes) => string;
  name: string;
  description?: string;
  options?: { alias: string; label: string }[];
  min?: number;
  max?: number;
}

export interface ISchema<TAttributes> {
  fields: IField<TAttributes>[];
}

interface IUpdateAttributes {
  [key: string]: string;
}

interface Props<TAttributes> {
  attributes: TAttributes;
  title?: string;
  schema: ISchema<TAttributes>;
  onSave?: (model: { [key: string]: string }) => Promise<void>;
}

function FormContainer<TAttributes>(props: Props<TAttributes>): JSX.Element {
  const formRef = useRef<Formsy>(null);
  const { attributes, onSave, schema } = props;
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formReadonly, setFormReadonly] = useState(true);

  const { t } = useTranslation('default');

  function getCurrentValues() {
    const formsyEl = formRef.current;
    return (formsyEl ? formsyEl.getCurrentValues() : {}) as IUpdateAttributes;
  }

  const handleCancel = () => {
    return setFormReadonly(true);
  };

  const disableButton = () => {
    console.log('disable button');
  };

  const enableButton = () => {
    console.log('enable button');
  };

  const handleSearchOrganisation = async (search: { query: string }) => {
    const resultWithCount = await GetOrganisations({
      pageIndex: 1,
      fullTextSearch: search.query || '',
      sortColumn: 'name',
    });
    return {
      data: resultWithCount.items || [],
    };
  };

  const renderLabelWithValue = (field: IField<TAttributes>, index: number) => {
    const { label } = field;
    const widget = renderField(field, index);
    return (
      <div
        key={label}
        style={{
          display: 'flex',
          lineHeight: '51px',
          borderBottom: '1px solid #f1f1f1',
        }}
      >
        <label
          style={{
            width: '200px',
            paddingLeft: '5px',
            fontSize: '12px',
            color: '#888',
          }}
        >
          {label}
        </label>
        <div style={{ flex: 1 }}>{widget}</div>
      </div>
    );
  };

  const renderField = (field: IField<TAttributes>, index: number) => {
    const { label = '', widget, name, description, readonly, formatter, options, min, max } = field;

    let initialValue = attributes ? attributes[name] : null;
    if (formReadonly) {
      initialValue = initialValue === 'SME' ? 'Business' : initialValue;
    } else {
      initialValue = initialValue === 'Business' ? 'SME' : initialValue;
    }

    const cannotEdit = typeof readonly === 'function' ? readonly(attributes) : readonly;
    if (formReadonly || cannotEdit) {
      const formatted = formatter ? formatter(initialValue, attributes) : initialValue;
      return formatted;
    }
    switch (widget) {
      case 'text': {
        const rules = {
          characterMax: 200,
          characterMin: 1,
        };
        const validationRules = generateTextValidation(rules);
        return (
          <TextFieldFormsy
            disabled={loading}
            key={index}
            type="text"
            name={name}
            label={label}
            value={initialValue}
            description={description}
            validations={validationRules.validations}
            validationErrors={validationRules.validationErrors}
          />
        );
      }
      case 'number': {
        const rules = { min, max };
        const validationRules = generateNumberValidation(rules);
        return (
          <TextFieldFormsy
            disabled={loading}
            key={index}
            type="number"
            name={name}
            label={label}
            value={initialValue}
            description={description}
            validations={validationRules.validations}
            validationErrors={validationRules.validationErrors}
          />
        );
      }
      case 'select': {
        return (
          <SelectFormsy
            key={index}
            disabled={loading}
            name={name}
            label={label}
            optionAlias={initialValue}
            description={description}
            variant="outlined"
            options={options || []}
          />
        );
      }
      case 'checkbox': {
        return (
          <CheckboxFormsy
            key={index}
            disabled={loading}
            name={name}
            value={initialValue}
            description={description}
            variant="outlined"
          />
        );
      }
      case 'imageUpload': {
        return (
          <ImageUploadFormsy
            key={name}
            required={false}
            disabled={loading}
            name={name}
            label={label}
            value={initialValue}
            placeholder={label}
            variant="outlined"
            extensions={'png,jpeg,jpg'.split(',')}
          />
        );
      }
      case 'organisationSelector': {
        const orgName = attributes ? attributes['organisationName'] : '';
        return (
          <RemoteChipSelectFormsy
            key={index}
            disabled={loading}
            name={name}
            label={label}
            placeholder={t('begin-typing-an-organisation')}
            description={description}
            onSearchOptions={handleSearchOrganisation}
            optionRenderer={({ id, name }: OrganisationDto) => ({
              alias: id,
              label: name,
            })}
            variant="outlined"
            value={{ alias: initialValue, label: orgName }}
          />
        );
      }
      default: {
        console.error('unsupported widget type');
        return <></>;
      }
    }
  };

  async function handleCreate(e: React.FormEvent) {
    e.stopPropagation();
    const model = getCurrentValues();
    setLoading(true);
    try {
      const transformed = transformOptionData(model);
      await (onSave && onSave(transformed as IUpdateAttributes));
      setFormReadonly(true);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  }

  return (
    <Root>
      {loading && <LinearProgress />}
      {error && (
        <Snackbar open={true} autoHideDuration={5000}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}
      <Formsy
        formElement="div"
        onValid={enableButton}
        onInvalid={disableButton}
        ref={formRef}
        className="form-container flex flex-col justify-center"
      >
        <Stack direction="column" spacing={1}>
          {schema.fields.map(renderLabelWithValue)}
        </Stack>
      </Formsy>
      <Stack direction="row" spacing={2} marginTop={3}>
        {(formReadonly && (
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => setFormReadonly(false)}
            color="primary"
          >
            {t('edit')}
          </Button>
        )) || (
          <>
            <Button variant="outlined" disabled={loading} onClick={handleCancel} color="primary">
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={loading}
              color="primary"
              autoFocus
              onClick={handleCreate}
            >
              {t('submit')}
            </Button>
          </>
        )}
      </Stack>
    </Root>
  );
}

export default FormContainer;
